import { useEffect, useState } from 'react'
import { object } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { post, get } from '@util/xhrAPI'
import Div from '@ui/layout/Div'
import Buttons from '@ui/elements/Buttons'
import TextareaType from '@ui/inputs/TextareaType'
import Alert from '@ui/elements/Alert'
import Form from '@ui/elements/Form'
import SwitcherType from '@ui/inputs/SwitcherType'
import TextType from '@ui/inputs/TextType'
import useToggle from '@hook/useToggle'
import useFormData from '@hook/useFormData'
import useDeleteButton from '@hook/useDeleteButton'

const MissionPreparation = ({ currentMissionPreparation = {} }) => {
  const { mission } = useSelector(state => state.main)
  const [t] = useTranslation('missionPreparation')
  const preparationInit = { equipments: [], comment: '', ...currentMissionPreparation, mission: mission.id }
  const equipmentInit = { name: '', isPacked: false }
  const [preparationObject, setPreparationObject] = useState(preparationInit)
  const [equipmentObject, setEquipmentObject] = useState({ ...equipmentInit })
  const renderFormData = useFormData()
  const [isLoading, toggleLoading] = useToggle(false)
  const dispatch = useDispatch()
  const deleteButton = useDeleteButton()

  const materialsManager = () => {
    if (!preparationObject.equipments.length) {
      return <Alert message={t('messages.empty_list')} type='is-info' />
    }
    return preparationObject.equipments.map(equipment => {
      return <Buttons buttons={{
        icon: equipment.isPacked ? 'icon-check_circle' : 'icon-hourglass_bottom',
        cssClass: `is-small ${equipment.isPacked ? 'is-success' : 'is-warning'}`,
        text: equipment.name,
        callback: () => setEquipmentObject(equipment)
      }}
        key={`equipment-${equipment.key}`}
      />
    })
  }

  const updatePreparationObject = e => {
    setEquipmentObject({ ...equipmentObject, [e.target.name]: e.target.value })
  }

  const saveEquipment = async () => {
    let equipments = []
    if ('undefined' === typeof (equipmentObject.key)) {
      equipments = [
        ...preparationObject.equipments,
        { ...equipmentObject, key: Math.random().toString(36).substring(2, 7) }
      ]
    } else {
      equipments = [
        ...preparationObject.equipments.filter(equipment => equipment.key !== equipmentObject.key),
        { ...equipmentObject }
      ]
    }

    await setPreparationObject({ ...preparationObject, equipments: [...equipments] })
    setEquipmentObject({ ...equipmentInit })
  }

  const savePreparation = async () => {
    await post(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-preparations`, renderFormData(preparationObject, ['equipments']), response => {
      toggleLoading()
      const { validationPage, code, errors } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.validated_successfuly') }))
        dispatch(updateValidationPage(validationPage))
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
      }
    })
    toggleLoading()
  }

  const getPreparation = async () => {
    await get(`${import.meta.env.VITE_API_URL}/api/missions/${mission.id}/mission-preparations`, response => {
      const { missionPreparation, code } = response
      if (200 === code && missionPreparation) {
        setPreparationObject(missionPreparation)
      }
    })
  }

  const removePreparation = async () => {
    await get(`${import.meta.env.VITE_API_URL}${preparationObject.links.remove}`, response => {
      const { code, errors } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.removed_successfuly') }))
        setPreparationObject(preparationInit)
      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
      }
    })
  }

  useEffect(() => {
    if (getPreparation() && !currentMissionPreparation) {
      setPreparationObject(preparationInit)
    }
  }, [])

  return (
    <Div cssClass='mission-preparation'>
      <h2 className='zone-title'>{t('titles.mission_preparation')}</h2>
      <Div cssClass='equipments columns'>
        <Div cssClass='column equipments-list'>
          <h3>{t(`titles.equipments_list`)}</h3>
          {materialsManager()}
        </Div>
        <Div cssClass='column equipment-form'>
          <h3>{t(`titles.edit_equipment`)}</h3>
          <Form>
            <TextType value={equipmentObject.name || ''} onChange={updatePreparationObject} label={t('labels.equipment_name')} name='name' isRequired={true} />
            <SwitcherType name='isPacked' value={parseInt(equipmentObject.isPacked)} onClick={updatePreparationObject} label={t('labels.is_packed')} />
            <Buttons buttons={{
              icon: 'icon-floppy-disk',
              cssClass: 'is-info is-small',
              callback: saveEquipment,
              text: t('actions.validate')
            }}
            />
          </Form>
        </Div>
      </Div>
      <TextareaType
        name='comment'
        onChange={e => setPreparationObject({ ...preparationObject, comment: e.target.value })}
        cssClass='preparation-comment'
        value={preparationObject.comment}
        placeholder={t('labels.comment_placeholder')}
        label={t('labels.comment')}
        isRequired={true}
      />

      <Div cssClass='actions-zone prepartion-actions'>
        <Buttons buttons={{
          icon: 'icon-floppy-disk',
          cssClass: `is-success is-small validate-preparation ${isLoading ? 'is-loading' : ''}`,
          callback: savePreparation,
          text: t('actions.validate_preparation')
        }}
        />
        {preparationObject.id && deleteButton(preparationObject.id, `Preparation:${mission.city}`, removePreparation, t('actions.remove_preparation'))}
      </Div>
    </Div>
  )
}

MissionPreparation.propTypes = {
  currentMissionPreparation: object
}

export default MissionPreparation
